/**
 * Font Loader
 *
 * Lazy load Google Fonts based on user selection
 */

import { FONT_FAMILIES, getGoogleFontsURL } from '../tokens/typography';

// ============================================================================
// TYPES
// ============================================================================

interface LoadedFonts {
  [fontId: string]: boolean;
}

// ============================================================================
// STATE
// ============================================================================

const loadedFonts: LoadedFonts = {};
let linkElement: HTMLLinkElement | null = null;

// ============================================================================
// FONT LOADING
// ============================================================================

/**
 * Load a font family from Google Fonts
 */
export const loadFont = (fontId: string): Promise<void> =>
  new Promise((resolve, reject) => {
    // Check if already loaded
    if (loadedFonts[fontId]) {
      resolve();
      return;
    }

    // Get font URL
    const url = getGoogleFontsURL(fontId);
    if (!url) {
      reject(new Error(`Font ID "${fontId}" not found`));
      return;
    }

    // Remove previous link if exists
    if (linkElement?.parentNode) {
      linkElement.parentNode.removeChild(linkElement);
    }

    // Create link element
    linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = url;

    // Handle load/error
    linkElement.onload = () => {
      loadedFonts[fontId] = true;
      resolve();
    };

    linkElement.onerror = () => {
      reject(new Error(`Failed to load font: ${fontId}`));
    };

    // Append to head
    document.head.appendChild(linkElement);
  });

/**
 * Preload multiple fonts
 */
export const preloadFonts = (fontIds: string[]): Promise<void[]> =>
  Promise.all(fontIds.map((id) => loadFont(id)));

/**
 * Check if font is loaded
 */
export const isFontLoaded = (fontId: string): boolean => loadedFonts[fontId] ?? false;

/**
 * Get all loaded fonts
 */
export const getLoadedFonts = (): string[] =>
  Object.keys(loadedFonts).filter((id) => loadedFonts[id]);

/**
 * Unload font (remove from document)
 */
export const unloadFont = (): void => {
  if (linkElement?.parentNode) {
    linkElement.parentNode.removeChild(linkElement);
    linkElement = null;
  }
};

/**
 * Load font with fallback
 * Returns font family CSS value or fallback if load fails
 */
export const loadFontSafe = async (fontId: string): Promise<string> => {
  const font = FONT_FAMILIES.find((f) => f.id === fontId);
  if (!font) {
    return 'sans-serif';
  }

  try {
    await loadFont(fontId);
    return `${font.family}, ${font.fallback}`;
  } catch {
    return font.fallback;
  }
};
